import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FilePdf, X, Plus, Warning, ClockCounterClockwise, LinkSimple, DownloadSimple, Eye, FloppyDisk, ArrowClockwise, GitDiff } from "@phosphor-icons/react";
import SnapshotPreview from "@/components/SnapshotPreview";
import SnapshotCompare from "@/components/SnapshotCompare";

/** Partner-reviewed snapshot flow:
 *   1. Open dialog → check for an in-progress draft (auto-saved from a prior session).
 *      If found, offer Resume; else immediately POST /playbook-snapshot/draft (AI writes it).
 *   2. Every edit is auto-saved (debounced ~1.5s) to /playbook-snapshot/current-draft so a
 *      slow AI call, browser close, or accidental cancel does not lose the partner's work.
 *   3. Export PDF → POST /playbook-snapshot/export → browser downloads. Server retires draft.
 */
export default function SnapshotDialog({ open, onClose, engagement }) {
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("draft"); // "draft" | "history"
  const [showPreview, setShowPreview] = useState(true);
  const [firmName, setFirmName] = useState("Yadid Advisory");
  const [savedAt, setSavedAt] = useState(null);   // ISO of last successful auto-save
  const [savingNow, setSavingNow] = useState(false);
  const [hasResumable, setHasResumable] = useState(false);  // server had a draft on open

  const skipNextSaveRef = useRef(true);  // skip auto-save on the very first draft-set from server
  const saveTimerRef = useRef(null);

  const loadHistory = () => {
    if (!engagement?.id) return;
    api.get(`/engagements/${engagement.id}/snapshots`).then((r) => setHistory(r.data)).catch(() => {});
  };

  const requestFreshAiDraft = async () => {
    setLoading(true); setError(""); setDraft(null); setHasResumable(false);
    skipNextSaveRef.current = true;
    try {
      const r = await api.post(`/engagements/${engagement.id}/playbook-snapshot/draft`);
      setDraft(r.data);
      setSavedAt(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not draft snapshot");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !engagement?.id) return;
    setDraft(null); setError(""); setTab("draft"); setSavedAt(null); setHasResumable(false);
    skipNextSaveRef.current = true;
    setLoading(true);

    // Check for a resumable draft first — if none, kick off a fresh AI draft.
    api.get(`/engagements/${engagement.id}/playbook-snapshot/current-draft`)
      .then((r) => {
        if (r.status === 204 || !r.data) {
          setLoading(false);
          requestFreshAiDraft();
        } else {
          setDraft({ title: r.data.title, subtitle: r.data.subtitle, sections: r.data.sections || [] });
          setSavedAt(r.data.updated_at || null);
          setHasResumable(true);
          setLoading(false);
        }
      })
      .catch(() => { setLoading(false); requestFreshAiDraft(); });

    loadHistory();
    api.get("/firm/settings").then((r) => setFirmName(r.data?.name || "Yadid Advisory")).catch(() => {});
    // eslint-disable-next-line
  }, [open, engagement?.id]);

  // Debounced auto-save — fires 1.2s after the last edit.
  useEffect(() => {
    if (!open || !draft || !engagement?.id) return;
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSavingNow(true);
      try {
        const r = await api.put(`/engagements/${engagement.id}/playbook-snapshot/current-draft`, {
          title: draft.title || null,
          subtitle: draft.subtitle || null,
          sections: (draft.sections || []).map((s) => ({
            label: s.label || "",
            summary: s.summary || "",
            highlights: (s.highlights || []).filter((h) => h !== undefined && h !== null),
          })),
        });
        setSavedAt(r.data?.updated_at || new Date().toISOString());
      } catch (err) {
        // Silent — the "Unsaved" indicator surfaces the failure; partner still keeps
        // in-memory state and the next debounced attempt will retry.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[snapshot-autosave] transient save error", err);
        }
      } finally {
        setSavingNow(false);
      }
    }, 1200);
    return () => saveTimerRef.current && clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line
  }, [draft, open, engagement?.id]);

  const updateSection = (i, patch) => {
    setDraft({ ...draft, sections: draft.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s) });
  };
  const updateHighlight = (i, hIdx, text) => {
    const sect = draft.sections[i];
    const next = [...(sect.highlights || [])];
    next[hIdx] = text;
    updateSection(i, { highlights: next });
  };
  const addHighlight = (i) => updateSection(i, { highlights: [...(draft.sections[i].highlights || []), ""] });
  const removeHighlight = (i, hIdx) => updateSection(i, { highlights: draft.sections[i].highlights.filter((_, k) => k !== hIdx) });
  const removeSection = (i) => setDraft({ ...draft, sections: draft.sections.filter((_, idx) => idx !== i) });

  const exportPdf = async () => {
    if (!draft) return;
    setExporting(true);
    try {
      const resp = await fetch(`${API}/engagements/${engagement.id}/playbook-snapshot/export`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: draft.title,
          subtitle: draft.subtitle,
          sections: (draft.sections || []).map((s) => ({
            label: s.label,
            summary: s.summary || "",
            highlights: (s.highlights || []).filter(Boolean),
          })),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Export failed (${resp.status})`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(engagement.name || "engagement").replace(/\s+/g, "_")}_snapshot.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Partner-facing snapshot exported");
      // Server-side draft is retired on export; clear local hint too.
      setHasResumable(false);
      setSavedAt(null);
      loadHistory();
      setTab("history");
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const discardDraft = async () => {
    if (!engagement?.id) return;
    if (!window.confirm("Discard the in-progress draft and start fresh with a new AI-drafted snapshot?")) return;
    try {
      await api.delete(`/engagements/${engagement.id}/playbook-snapshot/current-draft`);
      setSavedAt(null); setHasResumable(false);
      await requestFreshAiDraft();
      toast.success("Started a fresh AI draft");
    } catch (e) {
      toast.error("Could not discard draft");
    }
  };

  const shareLink = async (snapshotId) => {
    try {
      const { data } = await api.get(`/snapshots/${snapshotId}/share-link`);
      await navigator.clipboard.writeText(data.view_url);
      toast.success(`Shielded share link copied · valid ${data.expires_in_days} days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    }
  };

  const downloadHistorical = async (snap) => {
    try {
      const { data } = await api.get(`/snapshots/${snap.id}/share-link`);
      window.open(data.view_url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const savedLabel = savingNow
    ? "Saving…"
    : savedAt
    ? `Auto-saved · ${new Date(savedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : "Unsaved";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none bg-card max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="snapshot-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FilePdf size={16} weight="fill" className="text-primary" /> Playbook snapshot · client-facing draft
          </DialogTitle>
        </DialogHeader>

        <div className="flex border-b border-border -mt-2 mb-3">
          <button
            onClick={() => setTab("draft")}
            data-testid="snapshot-tab-draft"
            className={`text-[0.625rem] font-mono uppercase tracking-widest px-4 py-2 ${tab === "draft" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
          >Draft & export</button>
          <button
            onClick={() => setTab("history")}
            data-testid="snapshot-tab-history"
            className={`text-[0.625rem] font-mono uppercase tracking-widest px-4 py-2 flex items-center gap-1 ${tab === "history" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
          >
            <ClockCounterClockwise size={11} /> History ({history.length})
          </button>
          <button
            onClick={() => setTab("compare")}
            data-testid="snapshot-tab-compare"
            className={`text-[0.625rem] font-mono uppercase tracking-widest px-4 py-2 flex items-center gap-1 ${tab === "compare" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
          >
            <GitDiff size={11} /> Compare
          </button>
        </div>

        {tab === "compare" && (
          <SnapshotCompare engagementId={engagement?.id} refreshKey={history.length} history={history} />
        )}

        {tab === "history" && (
          <div className="space-y-2" data-testid="snapshot-history-list">
            {history.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">No approved snapshots yet — export one from the Draft tab to start the audit trail.</div>}
            {history.map((snap) => (
              <div key={snap.id} className="border border-border p-4 flex items-start justify-between gap-4" data-testid={`snapshot-history-${snap.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{snap.title || snap.engagement_name}</div>
                  <div className="text-[0.625rem] font-mono text-muted-foreground mt-0.5">
                    {String(snap.created_at || "").slice(0, 16).replace("T", " ")} UTC · by {snap.created_by_email || "—"} · {snap.sections?.length || 0} sections · {Math.round((snap.size_bytes || 0) / 1024)}kb
                  </div>
                  {snap.subtitle && <div className="text-xs text-muted-foreground truncate mt-1">{snap.subtitle}</div>}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => downloadHistorical(snap)}
                    data-testid={`snapshot-download-${snap.id}`}
                    title="Open PDF"
                    className="text-muted-foreground hover:text-primary p-2 border border-border hover:border-primary/60"
                  ><DownloadSimple size={14} weight="bold" /></button>
                  <button
                    onClick={() => shareLink(snap.id)}
                    data-testid={`snapshot-share-${snap.id}`}
                    title="Copy shielded partner org link"
                    className="text-muted-foreground hover:text-primary p-2 border border-border hover:border-primary/60"
                  ><LinkSimple size={14} weight="bold" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "draft" && <>
        <div className="border-l-2 border-primary/60 bg-primary/5 p-3 text-xs text-muted-foreground leading-relaxed" data-testid="snapshot-disclaimer">
          <Warning size={12} weight="fill" className="text-primary inline mr-1" />
          This is a <b>reviewed derivative</b>, not the internal playbook. Confidential notes, unapproved hypotheses, and relationship intelligence are excluded. Nothing here reaches the client until you edit and export it.
        </div>

        {hasResumable && draft && (
          <div className="border-l-2 border-primary/60 bg-secondary/40 p-3 text-xs mt-2 flex items-center justify-between gap-3" data-testid="snapshot-resume-banner">
            <div className="text-muted-foreground">
              Resumed your in-progress draft from {savedAt ? new Date(savedAt).toLocaleString() : "an earlier session"}. Every edit auto-saves.
            </div>
            <button
              onClick={discardDraft}
              data-testid="snapshot-discard-draft"
              className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow hover:text-eyebrow/80 inline-flex items-center gap-1 whitespace-nowrap"
            >
              <ArrowClockwise size={11} weight="bold" /> Start fresh
            </button>
          </div>
        )}

        {loading && <div className="text-sm text-muted-foreground py-8 text-center">YadidAi is drafting a client-safe summary…</div>}
        {error && <div className="text-sm text-destructive py-4">{error}</div>}

        {draft && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mt-2">
            {/* Editor (left, 3 cols) */}
            <div className="lg:col-span-3 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                  <span>Editor</span>
                  <span
                    data-testid="snapshot-save-status"
                    className={`inline-flex items-center gap-1 ${savingNow ? "text-primary" : savedAt ? "text-emerald-500" : "text-muted-foreground"}`}
                    title="Every edit auto-saves so you never lose work"
                  >
                    <FloppyDisk size={10} weight={savedAt ? "fill" : "regular"} /> {savedLabel}
                  </span>
                </div>
                <button
                  onClick={() => setShowPreview((v) => !v)}
                  data-testid="snapshot-toggle-preview"
                  className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow hover:text-eyebrow/80 inline-flex items-center gap-1"
                >
                  <Eye size={11} weight="bold" /> {showPreview ? "Hide preview" : "Show preview"}
                </button>
              </div>
              <div>
                <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Title</Label>
                <Input data-testid="snapshot-title" value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="rounded-none bg-secondary/40 mt-1" />
              </div>
              <div>
                <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Subtitle</Label>
                <Input data-testid="snapshot-subtitle" value={draft.subtitle || ""} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })} className="rounded-none bg-secondary/40 mt-1" />
              </div>

              <div className="space-y-4">
                {(draft.sections || []).map((s, i) => (
                  <div key={i} className="border border-border p-4 relative" data-testid={`snapshot-section-${i}`}>
                    <button onClick={() => removeSection(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-destructive" data-testid={`snapshot-remove-section-${i}`}>
                      <X size={14} />
                    </button>
                    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2">{s.label}</div>
                    <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Summary (client-facing)</Label>
                    <Textarea
                      data-testid={`snapshot-summary-${i}`}
                      rows={3}
                      value={s.summary || ""}
                      onChange={(e) => updateSection(i, { summary: e.target.value })}
                      className="rounded-none bg-secondary/40 mt-1 text-sm"
                    />
                    <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-3 block">Highlights</Label>
                    <div className="space-y-1.5 mt-1">
                      {(s.highlights || []).map((h, hIdx) => (
                        <div key={hIdx} className="flex gap-2 items-start">
                          <Textarea
                            data-testid={`snapshot-highlight-${i}-${hIdx}`}
                            rows={1}
                            value={h}
                            onChange={(e) => updateHighlight(i, hIdx, e.target.value)}
                            className="rounded-none bg-secondary/40 flex-1 text-sm resize-none"
                          />
                          <button onClick={() => removeHighlight(i, hIdx)} className="text-muted-foreground hover:text-destructive mt-1.5" data-testid={`snapshot-remove-highlight-${i}-${hIdx}`}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addHighlight(i)}
                        data-testid={`snapshot-add-highlight-${i}`}
                        className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow hover:text-eyebrow/80 flex items-center gap-1"
                      >
                        <Plus size={10} weight="bold" /> Add highlight
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Live PDF preview (right, 2 cols, sticky) */}
            {showPreview && (
              <div className="lg:col-span-2">
                <div className="lg:sticky lg:top-0">
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                    <Eye size={11} weight="bold" /> Live preview
                  </div>
                  <div className="bg-secondary/30 p-4">
                    <SnapshotPreview draft={draft} firmName={firmName} clientName={engagement?.client_name || engagement?.client?.name} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        </>}

        <DialogFooter className="border-t border-border pt-3 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          {tab === "draft" && (
            <Button onClick={exportPdf} disabled={!draft || exporting} data-testid="snapshot-export-btn" className="rounded-none bg-primary text-primary-foreground">
              <FilePdf size={14} weight="bold" className="mr-2" />
              {exporting ? "Rendering PDF…" : "Approve & export PDF"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
